/**
 * Running into a station.
 *
 * Everything the ship could hit until now was a sphere, and a distance check
 * answered it. A station is a flat slab with things bolted to the top of it,
 * and neither half is a sphere: a bounding sphere around a ring station is
 * mostly the hole in the middle.
 *
 * ── Two kinds of volume, tested separately ──────────────────────────────────
 *
 * The **deck** is its outline extruded by its thickness. Testing the real
 * polygon rather than a box matters because the shapes are rings, crosses and
 * spurs — a box around a ring is almost entirely empty space, and a ship would
 * bounce off nothing while crossing the hole.
 *
 * The **props** — towers, masts, dishes, modules — are boxes standing on the
 * deck, and they are already stored as boxes with a rotation. Folding them into
 * the deck volume would mean raising its roof to the height of the tallest
 * mast, which would put a wall over every landing pad.
 *
 * ── Why docking needs no special case ───────────────────────────────────────
 *
 * A capture volume sits *above* the plate, and the plate stands on the deck's
 * top face — so a ship inside one is already clear of the slab. Approaching a
 * berth therefore never touches the deck volume, and no exemption is needed to
 * keep docking possible. That is a property of the numbers rather than a
 * coincidence, and `dockVolume` is where it is decided.
 */
/**
 * Every solid part of a station, in the deck's own frame.
 *
 * Built from the geometry the renderer already draws from, so a station cannot
 * look like one thing and collide like another.
 */
export function stationVolumes(geometry) {
    const deckTop = geometry.thickness / 2;
    return [
        { kind: 'deck', outline: geometry.outline, thickness: geometry.thickness },
        ...geometry.props.map((prop) => ({
            kind: 'prop',
            position: prop.position,
            size: prop.size,
            rotation: prop.rotation,
            deckTop,
        })),
    ];
}
/**
 * Whether a point is inside a polygon, by the crossing rule.
 *
 * Ray-casting along +x and counting edges crossed. Handles the concave shapes
 * the generator produces — crosses and spurs — which a convex test would get
 * wrong in exactly the places a ship would try to fly through.
 */
function insideOutline(outline, x, y) {
    let inside = false;
    for (let i = 0, j = outline.length - 1; i < outline.length; j = i++) {
        const a = outline[i];
        const b = outline[j];
        const straddles = a.y > y !== b.y > y;
        if (!straddles)
            continue;
        const crossing = ((b.x - a.x) * (y - a.y)) / (b.y - a.y) + a.x;
        if (x < crossing)
            inside = !inside;
    }
    return inside;
}
/** Whether two segments cross. Shared endpoints and touching count. */
function segmentsCross(a1, a2, b1, b2) {
    const side = (p, q, r) => Math.sign((q.x - p.x) * (r.y - p.y) - (q.y - p.y) * (r.x - p.x));
    const d1 = side(a1, a2, b1);
    const d2 = side(a1, a2, b2);
    const d3 = side(b1, b2, a1);
    const d4 = side(b1, b2, a2);
    if (d1 !== d2 && d3 !== d4)
        return true;
    // Collinear and overlapping — rare, but a hull sliding exactly along an edge
    // is a thing players do on purpose when lining up on a pad.
    const on = (p, q, r) => Math.min(p.x, q.x) <= r.x &&
        r.x <= Math.max(p.x, q.x) &&
        Math.min(p.y, q.y) <= r.y &&
        r.y <= Math.max(p.y, q.y);
    if (d1 === 0 && on(a1, a2, b1))
        return true;
    if (d2 === 0 && on(a1, a2, b2))
        return true;
    if (d3 === 0 && on(b1, b2, a1))
        return true;
    if (d4 === 0 && on(b1, b2, a2))
        return true;
    return false;
}
/**
 * Whether two polygons share any area.
 *
 * Three ways they can: one contains a vertex of the other, either way round, or
 * their edges cross. All three are needed — containment alone misses a cross
 * shape overlapping a bar with no vertex inside either, and edge crossing alone
 * misses a small ship entirely within a large deck.
 *
 * Point-in-polygon is by the crossing rule, so a station with a hole in it is
 * still a station with a hole in it.
 */
function polygonsOverlap(a, b) {
    for (const point of a)
        if (insideOutline(b, point.x, point.y))
            return true;
    for (const point of b)
        if (insideOutline(a, point.x, point.y))
            return true;
    for (let i = 0, j = a.length - 1; i < a.length; j = i++) {
        for (let k = 0, l = b.length - 1; k < b.length; l = k++) {
            if (segmentsCross(a[j], a[i], b[l], b[k]))
                return true;
        }
    }
    return false;
}
/** A prop's four corners in the deck's frame, spun about its own centre. */
function propCorners(volume) {
    const cos = Math.cos(volume.rotation);
    const sin = Math.sin(volume.rotation);
    const hx = volume.size.x / 2;
    const hy = volume.size.y / 2;
    return [
        { x: -hx, y: -hy },
        { x: hx, y: -hy },
        { x: hx, y: hy },
        { x: -hx, y: hy },
    ].map((corner) => ({
        x: volume.position.x + corner.x * cos - corner.y * sin,
        y: volume.position.y + corner.x * sin + corner.y * cos,
    }));
}
/** The height range a volume occupies on the deck's up axis. */
function spanOf(volume) {
    return volume.kind === 'deck'
        ? { low: -volume.thickness / 2, high: volume.thickness / 2 }
        : { low: volume.deckTop, high: volume.deckTop + volume.size.z };
}
/** Whether this hull is inside a station volume. */
export function hitsVolume(volume, hull) {
    const span = spanOf(volume);
    if (hull.high < span.low || hull.low > span.high)
        return false;
    const shape = volume.kind === 'deck' ? volume.outline : propCorners(volume);
    return polygonsOverlap(hull.outline, shape);
}
/**
 * Which way to be pushed out of a volume, given how the hull got in.
 *
 * Off a face when the hull came down onto one, out through the side when it
 * arrived sideways — and the approach is what decides, not the geometry alone.
 * The two get the same answer in the ordinary cases and different answers in the
 * one that matters: a ring station.
 *
 * A hull descending through the hole in a ring, clipping the rim on its way, has
 * its centre over open space. Choosing by position alone calls that a sideways
 * problem and pushes sideways — which does nothing about the descent, so the
 * next frame arrives in exactly the same state, and the ship sits grinding
 * against the rim taking a fresh scrape every frame. It is not stuck in the
 * geometry; it is stuck being told the wrong way out.
 *
 * So the candidates are scored on how well they oppose the motion, and the last
 * resort is simply back the way it came, which cannot fail to work.
 *
 * `approach` is the hull's direction of travel in this volume's frame; a zero
 * vector means the caller does not know, and the geometry decides alone.
 * Returned in the deck's frame, never zero.
 */
export function pushOutOf(volume, hull, approach) {
    const at = hull.centre;
    const moving = approach && Math.hypot(approach.x, approach.y, approach.z) > 1e-6 ? approach : null;
    /** How well a direction resists the way the hull is going. Higher is better. */
    const opposes = (out) => moving ? -(out.x * moving.x + out.y * moving.y + out.z * moving.z) : 0;
    let chosen;
    if (volume.kind === 'deck') {
        const vertical = { x: 0, y: 0, z: at.z >= 0 ? 1 : -1 };
        const lateral = awayFromOutline(volume.outline, at.x, at.y);
        const over = insideOutline(volume.outline, at.x, at.y);
        if (!lateral)
            chosen = vertical;
        else if (!moving)
            chosen = over ? vertical : lateral;
        else
            chosen = opposes(vertical) >= opposes(lateral) ? vertical : lateral;
    }
    else {
        const middle = volume.deckTop + volume.size.z / 2;
        const dx = at.x - volume.position.x;
        const dy = at.y - volume.position.y;
        const dz = at.z - middle;
        // Whichever axis it is nearest the face of. A tower is tall and thin, so
        // sideways is usually the shortest way out and vertically almost never is.
        const out = { x: dx, y: dy, z: dz * 0.35 };
        const length = Math.hypot(out.x, out.y, out.z);
        chosen = length < 1e-6 ? { x: 0, y: 0, z: 1 } : { x: out.x / length, y: out.y / length, z: out.z / length };
    }
    // Nothing the shape suggested actually resists the motion. Back the way it
    // came always does, and a direction that does not is no direction at all.
    if (moving && opposes(chosen) <= 0) {
        const length = Math.hypot(moving.x, moving.y, moving.z);
        return { x: -moving.x / length, y: -moving.y / length, z: -moving.z / length };
    }
    return chosen;
}
/** The direction from the nearest point on an outline to this point. */
function awayFromOutline(outline, x, y) {
    let nearest = Infinity;
    let at = null;
    for (let i = 0, j = outline.length - 1; i < outline.length; j = i++) {
        const a = outline[i];
        const b = outline[j];
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const lengthSq = dx * dx + dy * dy;
        const t = lengthSq === 0 ? 0 : Math.max(0, Math.min(1, ((x - a.x) * dx + (y - a.y) * dy) / lengthSq));
        const px = a.x + dx * t;
        const py = a.y + dy * t;
        const gap = Math.hypot(x - px, y - py);
        if (gap < nearest) {
            nearest = gap;
            at = { x: px, y: py };
        }
    }
    if (!at || nearest < 1e-6)
        return null;
    return { x: (x - at.x) / nearest, y: (y - at.y) / nearest, z: 0 };
}
//# sourceMappingURL=stationHit.js.map